import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  Tooltip,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import analytics from '../../../../segmentTracking';
import CustomSelect from 'components/peoplePage/CustomSelect';
import { Input } from '../../../../components/Input/index';
import api from '../../../../utils/api';
import PaginationControls from '../AcademicManagement/components/PaginationControls';
import DeleteModal from './DeleteModal';

const InvitedUsersTab = ({ refreshTrigger }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalInvitations, setTotalInvitations] = useState(0);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [selectedInvitation, setSelectedInvitation] = useState({});
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const roleOptions = [
    { value: 'all', display: 'All roles' },
    { value: 'admin', display: 'Admin' },
    { value: 'member', display: 'Member' },
    { value: 'instructor', display: 'Instructor' },
  ];

  const handleSelectedRoleChange = (value) => {
    setSelectedRole(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const fetchInvitedUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { _id } = JSON.parse(localStorage.getItem('institution'));
      const skip = (currentPage - 1) * itemsPerPage;

      // Build query parameters
      const params = new URLSearchParams({
        institution: _id,
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add role filter if not 'all'
      if (selectedRole !== 'all') {
        params.append('role', selectedRole);
      }

      // Add search parameter if search term exists and is >= 2 characters
      if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
        params.append('search', debouncedSearchTerm);
      }

      const response = await api.get(
        `/dashboard/teams/invited?${params.toString()}`,
      );

      if (response?.data?.success) {
        setInvitedUsers(response?.data?.invitedUsers || []);
        setTotalInvitations(response?.data?.total || 0);
      } else {
        console.log('Network error');
        setInvitedUsers([]);
        setTotalInvitations(0);
      }
    } catch (error) {
      console.log(`Error -> ${error.message}`);
      setInvitedUsers([]);
      setTotalInvitations(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, selectedRole]);

  // Debounce search term - only trigger search after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced search if term is >= 2 characters or empty
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search status for tooltip feedback
  const searchStatus = useMemo(() => {
    if (searchTerm === '') {
      return { message: '', showTooltip: false };
    } else if (searchTerm.length < 2) {
      return {
        message: 'Type at least 2 characters to search...',
        showTooltip: true,
      };
    } else {
      return { message: '', showTooltip: false };
    }
  }, [searchTerm]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchInvitedUsers();
    }
  }, [refreshTrigger, fetchInvitedUsers]);

  useEffect(() => {
    fetchInvitedUsers();
  }, [fetchInvitedUsers]);

  const handleResendInvitation = async (invitationId, email, role) => {
    try {
      const currentInstitution = JSON.parse(
        localStorage.getItem('institution'),
      );

      let body = {
        institution: currentInstitution?._id,
        email: email,
        role: role,
      };

      await api.post(`/dashboard/users/inviteUser`, body);

      toast({
        title: 'Invite Resent',
        description: 'Invitation has been resent successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the list
      await fetchInvitedUsers();
    } catch (error) {
      console.log('resend error', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'An error occurred while resending the invitation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancelInvitation = async () => {
    try {
      // Track cancel invitation action
      analytics.track('settings:team:invitations:delete:submitted', {
        invitationId: selectedInvitation._id,
        inviteeEmail: selectedInvitation.email,
        cancellerEmail: currentUser.email,
        userRole: currentUser?.role,
      });

      const response = await api.delete(
        `/dashboard/teams/deleteInvitedMembers?userIds=${selectedInvitation._id}`,
      );

      if (response?.data?.success) {
        toast({
          title: 'Invitation Cancelled',
          description: 'Invitation has been cancelled successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Close the modal and refresh the list
        onDeleteClose();
        await fetchInvitedUsers();
      }
    } catch (error) {
      console.log('cancel error', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'An error occurred while cancelling the invitation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      {/* Manage Invited Users */}
      <Box>
        <Box>
          <Text fontSize={'20px'} fontWeight={'700'} lineHeight={'24px'}>
            Invited Users
          </Text>
          <Text fontSize={'14px'} color={'#757575'} mt={2}>
            Manage pending invitations and resend or cancel them as needed
          </Text>
        </Box>

        <Box mt={'24px'}>
          <Flex mb={4} alignItems="center">
            <Box mr={'8px'} position="relative" width="200px">
              <Tooltip
                label={searchStatus.message}
                isOpen={searchStatus.showTooltip}
                placement="bottom"
                bg="gray.700"
                color="white"
                fontSize="12px"
                px={3}
                py={2}
                borderRadius="6px"
                hasArrow
                gutter={8}
              >
                <Box width="100%">
                  <Input
                    placeholder="Search by email"
                    value={searchTerm}
                    className="TextInput"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      height: '40px',
                      border: '1.1px solid #E7E7E7',
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>

            <Box mr={'8px'}>
              <CustomSelect
                options={roleOptions}
                width="150px"
                height="40px"
                placeholder={'All roles'}
                value={selectedRole}
                onChange={(newRole) => handleSelectedRoleChange(newRole)}
              />
            </Box>

            <Box mr={'8px'}>
              <CustomSelect
                options={[
                  { value: 5, display: '5 per page' },
                  { value: 10, display: '10 per page' },
                  { value: 25, display: '25 per page' },
                  { value: 50, display: '50 per page' },
                ]}
                width="120px"
                height="40px"
                placeholder={'10 per page'}
                value={itemsPerPage}
                onChange={(newItemsPerPage) =>
                  handleItemsPerPageChange(newItemsPerPage)
                }
              />
            </Box>
          </Flex>

          <Box border={'1px solid #E7E7E7'} borderRadius={'12px'}>
            <Table>
              <Thead borderBottomWidth={2} borderBottomColor="#E7E7E7">
                <Tr>
                  <Th>Email</Th>
                  <Th borderLeft={'1px solid #E7E7E7'}>Role</Th>
                  <Th borderLeft={'1px solid #E7E7E7'}>Invited Date</Th>
                  {currentUser?.role === 'super_admin' ||
                  currentUser?.role === 'admin' ? (
                    <Th borderLeft={'1px solid #E7E7E7'}>Actions</Th>
                  ) : null}
                </Tr>
              </Thead>
              <Tbody>
                {invitedUsers?.map((invitation, index) => {
                  const capRole = invitation?.role
                    ?.split('_')
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase(),
                    )
                    .join(' ');

                  return (
                    <Tr key={index}>
                      <Td borderColor="#E7E7E7">{invitation?.email}</Td>
                      <Td borderColor="#E7E7E7">{capRole}</Td>

                      <Td borderColor="#E7E7E7">
                        {formatDate(invitation?.createdAt)}
                      </Td>
                      {currentUser?.role === 'super_admin' ||
                      currentUser?.role === 'admin' ? (
                        <Td borderColor="#E7E7E7">
                          <Flex gap={2} alignItems="center">
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              onClick={() =>
                                handleResendInvitation(
                                  invitation?._id,
                                  invitation?.email,
                                  invitation?.role,
                                )
                              }
                            >
                              Resend
                            </Button>
                            <DeleteIcon
                              cursor={'pointer'}
                              color={'red'}
                              onClick={() => {
                                // Track delete action click
                                analytics.track(
                                  'settings:team:invitations:delete:clicked',
                                  {
                                    invitationId: invitation._id,
                                    inviteeEmail: invitation.email,
                                    userRole: currentUser?.role,
                                    email: currentUser?.email,
                                  },
                                );
                                setSelectedInvitation(invitation);
                                onDeleteOpen();
                              }}
                            />
                          </Flex>
                        </Td>
                      ) : null}
                    </Tr>
                  );
                })}

                {invitedUsers?.length === 0 && !isLoading && (
                  <Tr>
                    <Td colSpan="5" textAlign="center" borderColor="#E7E7E7">
                      {debouncedSearchTerm || selectedRole !== 'all'
                        ? 'No invitations found matching your criteria.'
                        : 'No pending invitations found.'}
                    </Td>
                  </Tr>
                )}

                {isLoading && (
                  <Tr>
                    <Td colSpan="5" textAlign="center" borderColor="#E7E7E7">
                      Loading...
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={currentPage}
            totalItems={totalInvitations}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            entityName="invitations"
            analyticsPrefix="team:invitations"
            loading={isLoading}
          />
        </Box>
      </Box>
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        handleDelete={handleCancelInvitation}
      />
    </Box>
  );
};

export default InvitedUsersTab;
